import React, { MutableRefObject, useContext, useRef } from 'react';
import { useMutation } from 'react-query';
import { AvatarTypes } from '@chewy/pet-profile-pet-avatar';
import { useLocation } from 'react-router';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibRefreshIcon, KibHeartFillIcon } from '@chewy/kib-icons-react';
import { KibSpinner } from '@chewy/kib-interstitials-react';
import { PetInterface } from 'types/pet';
import { updatePetProfile } from 'api/petApi';
import AnalyticsContext from '../../../context/AnalyticsContext';
import log from '../../../util/log';
import FetchError from '../../../types/http';
import {
  ApiPetProfile,
  ApiPetProfileStatusEnum,
  ApiPetProfileStatusReasonEnum,
} from '../../../api/generated-client/PetProfile-Service-A.v1/models';
import { handleAuthError } from '../../../util/auth';
import ManagePetSupport from '../ManagePetSupport/ManagePetSupport';
import { ManagePetCard } from '../ManagePetCard/ManagePetCard';
import { getApiPetProfileFromInterface } from '../utils/getApiPetProfileFromInterface';
import { getReasonStepCopy, getSharedCopy } from '../copy';
import './manage-pet-reason.scss';

export type ManagePetReasonProps = {
  pet: PetInterface;
  statusChoice: ApiPetProfileStatusEnum;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;
  setReasonChoice: React.Dispatch<React.SetStateAction<ApiPetProfileStatusReasonEnum | undefined>>;
  setShowUndoConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setHeaderText: React.Dispatch<React.SetStateAction<string>>;
  setBodyText1: React.Dispatch<React.SetStateAction<string>>;
  setBodyText2: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setAvatarStroke: React.Dispatch<React.SetStateAction<AvatarTypes>>;
  returnToReason: boolean;
  setReturnToReason: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ManagePetReason = ({
  pet,
  statusChoice,
  setShowError,
  setReasonChoice,
  setShowUndoConfirmation,
  setHeaderText,
  setBodyText1,
  setBodyText2,
  setAvatarStroke,
  returnToReason,
  setReturnToReason,
}: ManagePetReasonProps) => {
  const { prompt, deceasedTitle, otherReasonTitle, optOutText, undoPrompt } = getReasonStepCopy(
    pet,
    statusChoice,
  );

  const { contactPrompt, contactLinkText, undoText } = getSharedCopy(pet);

  const location = useLocation();
  const analytics = useContext(AnalyticsContext);
  const choice: MutableRefObject<ApiPetProfileStatusReasonEnum | undefined> = useRef();
  const clientErrors: MutableRefObject<number> = useRef(0);

  const ownerName = pet?.ownerName ? pet.ownerName.split(' ')[0] : '';

  const updateCopy = (reasonChoice: ApiPetProfileStatusReasonEnum | undefined) => {
    switch (reasonChoice) {
      case ApiPetProfileStatusReasonEnum.Deceased:
        setHeaderText(`${ownerName}, we are so sorry for your loss`);
        setBodyText1(
          `Losing such an important part of the family is never easy, but we hope you find comfort in the cherished memories you and ${pet?.name} had together.`,
        );
        setBodyText2(`We're always here if you need anything.`);
        setAvatarStroke(AvatarTypes.MemorialDeceased);
        break;
      case ApiPetProfileStatusReasonEnum.Other:
        setHeaderText(`${ownerName}, thank you for letting us know`);
        setBodyText1(`Please take care, and know that we're always here if you need anything.`);
        setBodyText2(null);
        setAvatarStroke(null);
        break;
      case ApiPetProfileStatusReasonEnum.Unknown:
        setHeaderText(`${ownerName}, thank you for sharing ${pet?.name} with us`);
        setBodyText1(`Please take care, and know that we're always here if you need anything.`);
        setBodyText2(null);
        setAvatarStroke(null);
        break;
      // default to copy for UNKNOWN
      default:
        setHeaderText(`${ownerName}, thank you for sharing ${pet?.name} with us`);
        setBodyText1(`Please take care, and know that we're always here if you need anything.`);
        setBodyText2(null);
        setAvatarStroke(null);
    }
  };

  const updatePet = useMutation(
    (payload: ApiPetProfile) => updatePetProfile(Number(payload.id), payload),
    {
      onSuccess: () => {
        choice.current && setReasonChoice(choice.current);
      },
      onError: (error: FetchError) => {
        choice.current && setReasonChoice(choice.current); // if update fails, leave statusReason as UNKNOWN and move on
        log.error('Failed to Update Pet Profile:', `Status code: ${error.status}`, error);
        handleAuthError(error.status, location.pathname);
        if (error.status >= 500 || clientErrors.current >= 3) {
          setShowError(true);
        }
      },
    },
  );

  const undoUpdatePet = useMutation(
    (payload: ApiPetProfile) => updatePetProfile(Number(payload.id), payload),
    {
      onSuccess: () => {
        setShowUndoConfirmation(true);
      },
      onError: (error: FetchError) => {
        choice.current && setReasonChoice(choice.current); // if update fails, leave statusReason as UNKNOWN and move on
        log.error('Failed to Update Pet Profile:', `Status code: ${error.status}`, error);
        handleAuthError(error.status, location.pathname); // TODO check this
        if (error.status >= 500 || clientErrors.current >= 3) {
          setShowError(true);
        }
      },
    },
  );

  const isLoading = updatePet.isLoading;
  const undoIsLoading = undoUpdatePet.isLoading;

  const handleOnClick = (statusReason: ApiPetProfileStatusReasonEnum) => {
    returnToReason && setReturnToReason(false);
    updateCopy(statusReason);
    choice.current = statusReason;
    updatePet.mutate({
      ...getApiPetProfileFromInterface(pet),
      statusReason: statusReason,
    });
  };

  const handleUndoClick = () => {
    analytics.trackRainbow({
      name: 'pet profile rainbow - clicked',
      category: 'pet-profile-rainbow',
      action: 'click',
      label: 'pet_profile_rainbow_reason_undo',
    });
    undoUpdatePet.mutate({
      ...getApiPetProfileFromInterface(pet),
      status: ApiPetProfileStatusEnum.Active,
      statusReason: ApiPetProfileStatusReasonEnum.Other,
    });
  };

  return (
    <div className="manage-pet-reason">
      <p className="manage-pet-reason__prompt">{prompt}</p>
      <ManagePetCard
        className="manage-pet-reason__deceased-ingress"
        data-testid="manage-pet-reason__deceased-ingress"
        title={deceasedTitle}
        icon={<KibHeartFillIcon />}
        onClick={() => {
          analytics.trackRainbow({
            name: 'pet profile rainbow - clicked',
            category: 'pet-profile-rainbow',
            action: 'click',
            label: 'pet_profile_rainbow_passed',
          });
          handleOnClick(ApiPetProfileStatusReasonEnum.Deceased);
        }}
        loading={isLoading && choice.current === ApiPetProfileStatusReasonEnum.Deceased}
        disabled={isLoading}
      />
      <ManagePetCard
        className="manage-pet-reason__other-memorial"
        data-testid="manage-pet-reason__other-memorial"
        title={otherReasonTitle}
        onClick={() => {
          analytics.trackRainbow({
            name: 'pet profile rainbow - clicked',
            category: 'pet-profile-rainbow',
            action: 'click',
            label: 'pet_profile_rainbow_other',
          });
          handleOnClick(ApiPetProfileStatusReasonEnum.Other);
        }}
        loading={isLoading && choice.current === ApiPetProfileStatusReasonEnum.Other}
        disabled={isLoading}
      />
      <button
        className="manage-pet-reason__optout-option"
        data-testid="manage-pet-reason__optout-option"
        onClick={() => {
          analytics.trackRainbow({
            name: 'pet profile rainbow - clicked',
            category: 'pet-profile-rainbow',
            action: 'click',
            label: 'pet_profile_rainbow_not_say',
          });
          handleOnClick(ApiPetProfileStatusReasonEnum.Unknown);
        }}
      >
        {optOutText}
      </button>
      <div className="manage-pet-reason__footer">
        <div className="manage-pet-reason__undo">
          <span className="manage-pet-reason__undo-text">{undoPrompt}</span>
          <KibButtonNew
            className="manage-pet-reason__undo-button"
            data-testid="manage-pet-reason__undo-button"
            emphasis="tertiary"
            size="small"
            icon={undoIsLoading ? <KibSpinner /> : <KibRefreshIcon />}
            loading={undoIsLoading}
            onClick={() => handleUndoClick()}
          >
            {undoText}
          </KibButtonNew>
        </div>
        <ManagePetSupport
          className="manage-pet-support__manage-pet-reason"
          supportText={contactPrompt}
          supportLink={contactLinkText}
          onClick={() => {
            analytics.trackRainbow({
              name: 'pet profile rainbow - clicked',
              category: 'pet-profile-rainbow',
              action: 'click',
              label: 'pet_profile_rainbow_cs',
            });
          }}
        />
      </div>
    </div>
  );
};
